<template>
  <div class="select-seasons">
    <!-- Seasons list container -->
    <div
      :class="{
        'seasons-list-container': true,
        'seasons-list-container--empty': availableActiveFiltered.length === 0 && availableArchivedFiltered.length === 0,
        'select-seasons__all-seasons': true,
      }"
    >
      <fs-scrollable
        v-if="availableActiveFiltered.length || availableArchivedFiltered.length"
        class="seasons-list-container__scrollable-block"
      >
        <ul v-if="availableActiveFiltered.length" class="seasons-list-container__seasons-list">
          <fs-pill-season
            v-for="season in availableActiveFiltered"
            :key="season.id"
            class="seasons-list-container__season-pill"
            :season="season"
            :striped="season.partlySelected"
            size="medium"
            tag="li"
            is-add-mode
            hover-mode="add"
            @click="addSeason(season.id)"
          />
        </ul>

        <archived-seasons-section
          v-if="availableArchivedFiltered.length"
          class="select-seasons__archived-section"
          :force-expanded="!!searchQuery.trim()"
        >
          <ul class="seasons-list-container__seasons-list">
            <fs-pill-season
              v-for="season in availableArchivedFiltered"
              :key="season.id"
              class="seasons-list-container__season-pill"
              :season="season"
              :striped="season.partlySelected"
              size="medium"
              tag="li"
              is-add-mode
              hover-mode="add"
              @click="addSeason(season.id)"
            />
          </ul>
        </archived-seasons-section>
      </fs-scrollable>

      <no-results-found v-else size="small" text="No seasons found" />
    </div>
    <!-- / Seasons list container -->

    <fs-button
      v-if="canAddNewSeason"
      class="select-seasons__button-new-season"
      tertiary
      no-side-paddings
      size="tiny"
      tag="span"
      @click="newSeasonPopper?.toggle($event)"
    >
      <fs-button-icon :icon="addCircleIcon" color="dark-blue" top="0" />
      Create new season
    </fs-button>

    <fs-popper ref="newSeasonPopper" placement="right" :close-on-outside-click-check="clickWithinDatepicker">
      <add-new-tag-form
        mode="season"
        :starting-name="searchQuery"
        :add-tag-func="addNewSeason"
        @cancel="newSeasonPopper?.close($event)"
      />
    </fs-popper>

    <template v-if="selectedSeasons.length">
      <div class="select-seasons__divider"></div>

      <!-- Selected seasons -->
      <div
        :class="{
          'selected-seasons': true,
          'select-seasons__selected-seasons': true,
        }"
      >
        <div class="selected-seasons__title">{{ selectedLabel }}</div>

        <!-- Seasons list container -->
        <div
          :class="{
            'seasons-list-container': true,
            'seasons-list-container--empty': useSeasons().loaded.length === 0,
            'selected-seasons__seasons-list': true,
          }"
        >
          <fs-scrollable class="seasons-list-container__scrollable-block">
            <ul class="seasons-list-container__seasons-list">
              <fs-pill-season
                v-for="season in selectedSeasons"
                :key="season.id"
                class="seasons-list-container__season-pill"
                :season="season"
                :striped="season.partlySelected"
                size="medium"
                tag="li"
                hover-mode="clear"
                @click="removeSeason(season.id)"
              />
            </ul>
          </fs-scrollable>
        </div>
        <!-- / Seasons list container -->
      </div>
      <!-- / Selected seasons -->
    </template>
  </div>
</template>

<script setup lang="ts">
import { useSeasons } from '@/store/seasons';
import AddNewTagForm from '@components/tags-edit-modal/add-new-tag-form.vue';
import FsButton from '@components/buttons/fs-button.vue';
import FsButtonIcon from '@components/fs-button-icon.vue';
import FsPillSeason from '@components/pills/fs-pill-season.vue';
import FsPopper from '@components/poppers/fs-popper.vue';
import FsScrollable from '@components/fs-scrollable.vue';
import NoResultsFound from '@components/no-results-found.vue';
import { computed, ref } from 'vue';
import type { SeasonDto } from '@/ApiClient';
import addCircleIcon from '@icons/other/add-circle.svg';
import ArchivedSeasonsSection from '@components/season/archived-seasons-section.vue';
import { dateToDateOnlyString } from '@lib/formatters';

export type SelectedSeason = SeasonDto & {
  partlySelected?: boolean;
};

const {
  searchQuery,
  selectedSeasons,
  availableSeasons,
  selectedLabel = 'Selected seasons',
  canAddNewSeason = true,
} = defineProps<{
  searchQuery: string;
  selectedSeasons: SelectedSeason[];
  availableSeasons: SelectedSeason[];
  selectedLabel?: string;
  canAddNewSeason?: boolean;
}>();

const emits = defineEmits<{
  'add-season': [number];
  'remove-season': [number];
}>();

const newSeasonPopper = ref<any | null>(null);

const availableActiveFiltered = computed(() => {
  const query = searchQuery.trim().toLowerCase();
  return availableSeasons
    .filter((season) => !season.isArchived)
    .filter((season) => season.name?.toLowerCase().includes(query));
});

const availableArchivedFiltered = computed(() => {
  const query = searchQuery.trim().toLowerCase();
  return availableSeasons
    .filter((season) => season.isArchived)
    .filter((season) => season.name?.toLowerCase().includes(query));
});

const addSeason = (id: number) => {
  emits('add-season', id);
};

const removeSeason = (id: number) => {
  emits('remove-season', id);
};

async function addNewSeason(
  name: string,
  color: string,
  categoryId: number,
  channelActivities?: Record<number, [Date, Date] | undefined>
) {
  const channelActivitiesArgs = channelActivities
    ? Object.entries(channelActivities)
        .filter(([_, dates]) => dates && dates[0] && dates[1])
        .map(([channelId, dates]) => ({
          channelId: parseInt(channelId),
          start: dateToDateOnlyString(dates![0]),
          end: dateToDateOnlyString(dates![1]),
        }))
    : undefined;

  const addedSeason = await useSeasons().create({
    name,
    color,
    channelActivities: channelActivitiesArgs,
  });
  addSeason(addedSeason.id);
}

const clickWithinDatepicker = (target: HTMLElement) => {
  const $container = document.querySelector('.mx-datepicker-popup');

  if (!$container) return false;

  return $container.contains(target);
};
</script>

<style scoped lang="scss">
@import '@styles/variables';
@import '@styles/text';

// Seasons list container -----------------------------------------------------
.seasons-list-container {
  display: flex;
  justify-content: center;
  align-items: center;

  &--empty {
    height: 3rem;
  }

  &__scrollable-block {
    width: calc(100% + 0.6rem);
    min-width: calc(100% + 0.6rem);
    max-height: 25rem;
  }

  &__seasons-list {
    padding: 0;
    margin: 0;
    display: flex;
    justify-content: flex-start; // needed for horizontal centering of the items list
    flex-wrap: wrap;
    list-style: none;

    column-gap: 1rem;
    row-gap: 1.4rem;
  }

  &__season-pill {
    width: 8rem;
  }
}

// Selected seasons -----------------------------------------------------------
.selected-seasons {
  &__title {
    @include overline--subtle;
    margin-bottom: 3px;
  }

  &__seasons-list {
    min-width: 100%;
    align-self: flex-start;
  }
}

// Seasons dropdown -----------------------------------------------------------
.select-seasons {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__all-seasons {
    min-width: 100%;
    margin-bottom: 1rem;
    align-self: flex-start;
  }

  &__button-new-season {
    margin-bottom: 1rem;
    align-self: flex-start;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__archived-section {
    margin-top: 1rem;
  }

  &__divider {
    width: calc(100% + 3.2rem);
    margin: 0 0 1.2rem 0;
    height: 1px;
    background: rgba($c-dark, 0.1);
  }

  &__selected-seasons {
    width: 100%;
  }
}
</style>
